import React, { createContext, HtmlHTMLAttributes, useContext } from 'react';
import { ConfigContext, CURRENCY_BY_CODE } from 'src/components/AppShell/ConfigProvider';

import classes from './SimplePrice.scss';

// Orders are shown in the currency they were placed in, not the one the customer is browsing in
export const PriceCurrencyContext = createContext<string | null | undefined>(null);

type SimplePriceProps = {
    price: string | number;
    priceIncl: string | number;
    priceHighest?: number;
    priceHighestIncl?: number;
    vat?: boolean;
    crossedOutPrice?: boolean;
    // Overrides PriceCurrencyContext and the display currency
    currencyCode?: string | null;
} & HtmlHTMLAttributes<any>;

export const Price: React.FC<SimplePriceProps> = (props) => {
    const {
        price,
        priceIncl,
        priceHighest,
        priceHighestIncl,
        vat = false,
        crossedOutPrice = false,
        currencyCode: fixedCurrencyCode,
        ...rest
    } = props;
    const { amount, prefix, symbol, high, currencyCode } = usePrice({
        price,
        priceIncl: priceIncl ?? price,
        vat,
        priceHighest,
        priceHighestIncl,
        currencyCode: fixedCurrencyCode,
    });

    return (
        <span {...rest}>
            {prefix}
            {symbol}
            <span itemProp={crossedOutPrice ? '' : 'price'}>{amount} </span>
            {(priceHighest || priceHighestIncl) && high !== amount ? `- ${symbol}${high}` : ''}
            {!crossedOutPrice && <meta itemProp="priceCurrency" content={currencyCode} />}
        </span>
    );
};

type DisplayPrice = {
    prefix: string;
    symbol: string;
    amount: string;
    high: string;
    currencyCode: string;
};

type UsePriceOptions = {
    price: number | string;
    priceIncl?: number | string;
    vat: boolean;
    priceHighest?: number;
    priceHighestIncl?: number;
    // Overrides PriceCurrencyContext and the display currency
    currencyCode?: string | null;
};

export function usePrice({
    price,
    priceIncl = price,
    vat,
    priceHighest,
    priceHighestIncl,
    currencyCode: fixedCurrencyCode,
}: UsePriceOptions): DisplayPrice {
    const roundNumber = (num: number) => {
        return Math.round((num + Number.EPSILON) * 100) / 100;
    };

    const { currentCurrency: displayCurrency } = useContext(ConfigContext);
    const contextCurrencyCode = useContext(PriceCurrencyContext);
    const orderCurrencyCode = fixedCurrencyCode ?? contextCurrencyCode;
    const currentCurrency = (orderCurrencyCode && CURRENCY_BY_CODE[orderCurrencyCode]) || displayCurrency;
    const subjectPrice = vat ? priceIncl : price;
    const symbol = currentCurrency.symbol;
    const float = parseFloat(String(subjectPrice));
    const prefix = float < 0 ? '-' : '';
    const amount = Math.abs(roundNumber(float)).toLocaleString('en-EN', {
        minimumFractionDigits: 2,
    });
    const currencyCode = currentCurrency.code;

    const high = Math.abs(vat ? roundNumber(priceHighestIncl!) : roundNumber(priceHighest!)).toLocaleString('en-EN', {
        minimumFractionDigits: 2,
    });
    return { symbol, prefix, amount, high, currencyCode };
}

export function usePriceString(price: number | string, vat: boolean): string {
    const { symbol, prefix, amount } = usePrice({ price, vat });
    return `${prefix}${symbol}${amount}`;
}

export const StyledPrice: React.FC<SimplePriceProps> = (props) => {
    return <Price className={classes.root} price={props.price} vat={props.vat} priceIncl={props.priceIncl} />;
};

export const StyledText: React.FC<React.PropsWithChildren> = (props) => {
    return <span className={classes.root}>{props.children}</span>;
};
